import posthog from 'posthog-js';

import { config } from '@/config';
import { appEvents } from '@/services/appEvents';
import { useConsentStore } from '@/services/consent';

import type { WafProtectedRequest } from '@/modules/auth/services/waf';
import type { User } from '@/modules/auth/types';
import type { AnalyticsConsent } from '@/services/consent';

/** Every product event the app sends, with the properties it carries. */
interface AnalyticsEvent {
  agent_message_submitted: undefined;
  custom_area_created: { project_id: number | null; has_site: boolean };
  custom_area_deleted: { custom_area_id: number };
  custom_areas_imported: { file_format: string; created_count: number; failed_count: number };
  layer_download_requested: { layer_type: string; download_format: string };
  layers_selection_confirmed: { selected_layer_count: number };
  map_basemap_selected: { basemap: string };
  map_compare_started: undefined;
  map_pixel_inspection_activated: undefined;
  map_location_link_copied: undefined;
  project_selected: { project_id: number };
  site_selected: { project_id: number; site_id: number };
  survey_boundary_clicked: { survey_boundary_id: number };
  target_area_clicked: { target_area_id: number };
  restoration_potential_chapter_viewed: undefined;
  onboarding_modal_opened: { trigger: string };
  onboarding_modal_closed: { reason: string; completed: boolean };
  tour_started: { tour: string };
  tour_step_completed: { tour: string; step: string };
  tour_skipped: { tour: string; step: string };
  tour_completed: { tour: string };
  waf_captcha_shown: { request: WafProtectedRequest };
  waf_captcha_solved: { request: WafProtectedRequest };
  waf_captcha_cancelled: { request: WafProtectedRequest };
  /** The WAF turned a request away, or turned away its one retry, and the app gave up. */
  waf_request_refused: { request: WafProtectedRequest; action: 'captcha' | 'challenge' | 'block' };
  contact_cta_clicked: { source: string; topic?: string };
  stage_two_cta_clicked: { source: string };
  contact_request_submitted: { source: string };
}

/** Whether `posthog.init` has run. It runs in every build with a key. */
let started = false;
/** Kept so a visitor who opts in mid-session is identified then. */
let currentUser: User | null = null;

/** PostHog only runs in builds with a key. */
export function isAnalyticsConfigured() {
  const { key, host } = config.posthog;
  return import.meta.env.PROD && !!key && !!host;
}

/**
 * Whether PostHog runs with cookies and an identity. Only an explicit opt-in does: a visitor who
 * hasn't chosen yet (the signup page) or has opted out is counted anonymously, without cookies.
 */
function optedIn() {
  return started && useConsentStore.getState().analytics === 'granted';
}

export function initAnalytics() {
  if (!isAnalyticsConfigured()) return;

  startPostHog();
  applyConsent(useConsentStore.getState().analytics);
  useConsentStore.subscribe((state, previous) => {
    if (state.analytics !== previous.analytics) applyConsent(state.analytics);
  });
  forwardAppEvents();
}

function startPostHog() {
  const { key, host } = config.posthog;
  // A browser's stored opt-in may be out of date: the user can opt out from another browser,
  // and only whoami knows. Forgetting it before init starts every page load cookieless, and
  // whoami opts back in when the account still agrees.
  forgetPostHogConsent();
  posthog.init(key, {
    api_host: host,
    // Named here so `forgetPostHogConsent` knows where PostHog keeps it. This is PostHog's default.
    consent_persistence_name: posthogConsentName(),
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    // Anyone who hasn't opted in is captured in cookieless mode: no cookies or storage, no
    // identity, and no session replay, with PostHog hashing a daily anonymous ID server-side.
    // `on_reject` alone captures nothing until a choice is made; treating "not yet chosen" as
    // rejected is what lets the signup page send anonymous pageviews.
    cookieless_mode: 'on_reject',
    opt_out_capturing_by_default: true,
    // Replay only runs for opted-in users, and never records what is typed.
    session_recording: { maskAllInputs: true },
  });
  started = true;
}

/**
 * Bring PostHog's own consent record (in localStorage) into line with the account's. Until
 * whoami answers (`null`), PostHog stays undecided, and so cookieless.
 */
function applyConsent(consent: AnalyticsConsent) {
  const status = posthog.get_explicit_consent_status();
  if (consent === 'granted' && status !== 'granted') {
    posthog.opt_in_capturing({ captureEventName: false });
  }
  if (consent === 'denied' && status !== 'denied') posthog.opt_out_capturing();
  // Cookieless mode stops writing PostHog's storage but leaves whatever was already there.
  if (posthog.get_explicit_consent_status() !== 'granted') clearPostHogStorage();
  // Staging and production report into the same project, so tag every event with
  // the build mode to keep the two apart. Re-registered because a consent change drops it.
  posthog.register({ environment: config.posthog.environment });
  if (consent === 'granted' && currentUser) identifyUser(currentUser);
}

function clearPostHogStorage() {
  if (!hasPostHogStorage()) return;
  posthog.persistence?.remove();
  posthog.sessionPersistence?.remove();
}

function posthogConsentName() {
  return `__ph_opt_in_out_${config.posthog.key}`;
}

function forgetPostHogConsent() {
  try {
    localStorage.removeItem(posthogConsentName());
  } catch {
    // Storage blocked: PostHog can't have kept a record there either.
  }
}

/** PostHog's own persistence key, in its cookie or localStorage. */
function hasPostHogStorage() {
  const name = `ph_${config.posthog.key}_posthog`;
  try {
    return document.cookie.includes(`${name}=`) || localStorage.getItem(name) !== null;
  } catch {
    return false;
  }
}

/** App events forwarded to PostHog. */
function forwardAppEvents() {
  appEvents.subscribe((event) => {
    switch (event.type) {
      case 'onboarding.modal_opened':
        trackEvent('onboarding_modal_opened', event.payload);
        break;
      case 'onboarding.modal_closed':
        trackEvent('onboarding_modal_closed', event.payload);
        break;
      case 'tour.started':
        trackEvent('tour_started', event.payload);
        break;
      case 'tour.step_completed':
        trackEvent('tour_step_completed', event.payload);
        break;
      case 'tour.skipped':
        trackEvent('tour_skipped', event.payload);
        break;
      case 'tour.completed':
        trackEvent('tour_completed', event.payload);
        break;
      case 'contact.requested':
        trackEvent('contact_cta_clicked', event.payload);
        break;
      case 'stage_two.requested':
        trackEvent('stage_two_cta_clicked', event.payload);
        break;
      case 'contact.submitted':
        trackEvent('contact_request_submitted', event.payload);
        break;
    }
  });
}

export function trackEvent<E extends keyof AnalyticsEvent>(
  event: E,
  properties?: AnalyticsEvent[E]
) {
  // Sent with cookies or without, as the visitor's choice decides.
  if (started) posthog.capture(event, properties);
}

/**
 * An error the app caught and handled, so PostHog's unhandled-error capture never sees it.
 * Logged to the console in builds without PostHog.
 */
export function captureException(error: unknown, properties?: Record<string, unknown>) {
  if (started) posthog.captureException(error, properties);
  else console.error('Error captured:', error, properties);
}

/** Never for a visitor who hasn't opted in: cookieless events carry no identity. */
export function identifyUser(user: User) {
  currentUser = user;
  if (!optedIn()) return;

  const distinctId = String(user.id);
  const identifiedUserId = posthog.get_property('$user_id');
  if (identifiedUserId && identifiedUserId !== distinctId) posthog.reset();

  posthog.identify(distinctId, {
    email: user.email,
    name: user.display_name || user.displayable_name || user.username,
  });
}

/**
 * On sign-out: forget the user, their choice and PostHog's copy of it, along with PostHog's
 * cookie and storage, so whoever uses this browser next starts undecided and cookieless.
 */
export function resetUser() {
  currentUser = null;
  useConsentStore.getState().clearAnalytics();
  if (!started) return;
  // reset() drops the identity and the consent record, but saves a fresh anonymous ID straight
  // back; going back to pending turns persistence off, which deletes it.
  posthog.reset();
  posthog.clear_opt_in_out_capturing();
  clearPostHogStorage();
}
